
import { Settlement } from "@/types/payment";
import { Expense } from "@/types/expense";

/**
 * Calculates balances for all members based on expenses and paid settlements
 * @param expenses List of expenses to calculate balances from
 * @param paidSettlements List of already paid settlements to consider
 * @returns A Map of member IDs to their current balance
 */
export const calculateMemberBalances = (
  expenses: Expense[],
  paidSettlements: Settlement[]
): Map<string, number> => {
  if (!expenses || expenses.length === 0) {
    console.log('No expenses to calculate balances from');
    return new Map<string, number>();
  }
  
  // Create a map to track each member's balance
  const balances = new Map<string, number>();
  
  // First, calculate each person's balance based on expenses
  expenses.forEach(expense => {
    const amount = expense.jpy_amount || expense.amount;
    const sharedCount = expense.sharedWith.length;
    
    if (sharedCount === 0) {
      console.log('Warning: Expense has no shares:', expense);
      return; // Skip this expense if it has no shares
    }
    
    const perPersonAmount = amount / sharedCount;
    
    // The person who paid gets credit for the full amount
    const paidByPrevious = balances.get(expense.paidBy) || 0;
    balances.set(expense.paidBy, paidByPrevious + amount);
    
    // Each person who shared the expense gets a debit for their portion
    expense.sharedWith.forEach(memberId => {
      const memberPrevious = balances.get(memberId) || 0;
      balances.set(memberId, memberPrevious - perPersonAmount);
    });
  });
  
  // Apply paid settlements to balances
  paidSettlements.forEach(settlement => {
    // Person who paid (from) gets a credit (increase balance)
    const fromPrevious = balances.get(settlement.from) || 0;
    balances.set(settlement.from, fromPrevious + settlement.amount);
    
    // Person who received (to) gets a debit (decrease balance)
    const toPrevious = balances.get(settlement.to) || 0;
    balances.set(settlement.to, toPrevious - settlement.amount);
  });
  
  // Round balances to avoid floating point issues (JPY has no cents)
  balances.forEach((balance, memberId) => {
    balances.set(memberId, Math.round(balance));
  });
  
  console.log('Final balances after all calculations:', Object.fromEntries(balances));
  return balances;
};

/**
 * Categorizes members into debtors and creditors based on their balances
 * @param balances Map of member IDs to their balances
 * @returns Object containing arrays of debtors and creditors
 */
export const categorizeMembersByBalance = (
  balances: Map<string, number>
): { debtors: [string, number][]; creditors: [string, number][]; } => {
  const debtors: [string, number][] = [];
  const creditors: [string, number][] = [];
  
  balances.forEach((balance, memberId) => {
    if (balance < -1) { // Using threshold to avoid tiny settlements
      debtors.push([memberId, -balance]); // Convert to positive debt amount
    } else if (balance > 1) { // Using threshold to avoid tiny settlements
      creditors.push([memberId, balance]);
    }
    // Members with ~0 balance are ignored
  });
  
  // Sort debtors and creditors by amount (largest first) for more efficient settlements
  debtors.sort((a, b) => b[1] - a[1]);
  creditors.sort((a, b) => b[1] - a[1]);
  
  return { debtors, creditors };
};

/**
 * Generates optimized settlement transactions using the minimum number of transfers
 * @param debtors Members who owe money (negative balance)
 * @param creditors Members who are owed money (positive balance)
 * @returns Array of Settlement objects
 */
export const generateOptimizedSettlements = (
  debtors: [string, number][],
  creditors: [string, number][]
): Settlement[] => {
  const settlements: Settlement[] = [];
  
  // For each debtor, find optimal creditors to minimize the number of transactions
  while (debtors.length > 0 && creditors.length > 0) {
    const [debtorId, debtAmount] = debtors[0];
    const [creditorId, creditAmount] = creditors[0];
    
    // Calculate settlement amount as the minimum of the debt and credit
    const settlementAmount = Math.min(debtAmount, creditAmount);
    
    if (settlementAmount > 0) {
      settlements.push({
        from: debtorId,
        to: creditorId,
        amount: settlementAmount,
        currency: 'JPY',
        isPaid: false
      });
      
      // Update remaining balances
      if (debtAmount > creditAmount) {
        // Debtor still has debt, creditor is fully paid
        debtors[0] = [debtorId, debtAmount - creditAmount];
        creditors.shift(); // Remove this creditor
      } else if (creditAmount > debtAmount) {
        // Creditor still has credit, debtor is fully paid
        creditors[0] = [creditorId, creditAmount - debtAmount];
        debtors.shift(); // Remove this debtor
      } else {
        // Both are exactly settled
        debtors.shift();
        creditors.shift();
      }
    } else {
      // This should never happen with our thresholds, but just in case
      console.warn('Zero settlement amount calculated');
      break;
    }
  }
  
  return settlements;
};
